/**
 * Country & Locale Switcher Web Component
 *
 * Handles country and language selection with localStorage persistence.
 * Works in both desktop (tabs) and mobile (details) modes.
 *
 * @author Pocketbook International SA
 */

class CountryLocaleSwitcher extends HTMLElement {
  constructor() {
    super();

    // Get initial state from data attributes (SSR)
    this.currentLocale = this.dataset.currentLocale;
    this.currentCountry = this.dataset.currentCountry;
    this.storeId = this.dataset.storeId;

    // Get asset base URL from data attribute (set by Liquid)
    this.assetBaseUrl = this.dataset.assetBaseUrl || '';

    // Test mode flag (uses preview URLs instead of production URLs)
    this.testMode = this.dataset.testMode === 'true';

    // Storage keys
    this.STORAGE_KEY = 'inkposter_preferences';

    // Available locales (loaded from config)
    this.availableLocales = [];

    // Current store configuration
    this.currentStoreConfig = null;

    // Search optimization with cache size limit
    this.searchCache = new Map();
    this.searchDebounceTimer = null;
    this.SEARCH_DEBOUNCE_MS = 150;
    this.MAX_RESULTS = 12;
    this.SEARCH_CACHE_MAX_SIZE = 50; // Limit cache to prevent memory leaks

    // Elements
    this.details = null;
    this.tabby = null;

    // Bound event handlers for cleanup
    this._handlePopstate = this.handleBrowserNavigation.bind(this);
    this._handlePageshow = this._onPageShow.bind(this);

    // Cached store configurations to avoid repeated fetches
    this._storeConfigCache = null;

    // Debug mode - controlled via data-debug attribute or localStorage
    // Priority: data-debug > localStorage > default (false)
    this.DEBUG = this.getDebugMode();

    // Log test mode activation (always show regardless of debug)
    if (this.testMode) {
      console.log('🧪 [CountryLocaleSwitcher] Test mode enabled - using preview URLs');
    }

    if (this.DEBUG) {
      console.log('🔧 [CountryLocaleSwitcher] Debug mode enabled');
    }

    this.log('🚀 Constructor called', {
      currentLocale: this.currentLocale,
      currentCountry: this.currentCountry,
      storeId: this.storeId,
      testMode: this.testMode,
      debug: this.DEBUG,
    });
  }

  /**
   * Get debug mode from data attribute or localStorage
   * @returns {boolean} True if debug mode is enabled
   */
  getDebugMode() {
    // Priority 1: data-debug attribute
    if (this.dataset.debug !== undefined) {
      return this.dataset.debug === 'true';
    }

    // Priority 2: localStorage (for persistent debugging)
    try {
      const stored = localStorage.getItem('inkposter_debug');

      if (stored !== null) {
        return stored === 'true';
      }
    } catch {
      // localStorage not available
    }

    // Priority 3: default - false for production
    return false;
  }

  /**
   * Enable or disable debug mode (can be called from console)
   * @param {boolean} enabled - Whether to enable debug mode
   */
  setDebugMode(enabled) {
    this.DEBUG = enabled;

    try {
      if (enabled) {
        localStorage.setItem('inkposter_debug', 'true');
        console.log('🔧 [CountryLocaleSwitcher] Debug mode ENABLED - will persist across page loads');
      } else {
        localStorage.removeItem('inkposter_debug');
        console.log('🔧 [CountryLocaleSwitcher] Debug mode DISABLED');
      }
    } catch {
      console.log(`🔧 [CountryLocaleSwitcher] Debug mode ${enabled ? 'ENABLED' : 'DISABLED'} (session only)`);
    }
  }

  /**
   * Debug logger - only logs when DEBUG is true
   * @param {string} message - Log message
   * @param {any} data - Optional data to log
   */
  log(message, data = null) {
    if (!this.DEBUG) return;

    const prefix = '[CountryLocaleSwitcher]';

    if (data !== null) {
      console.log(`${prefix} ${message}`, data);
    } else {
      console.log(`${prefix} ${message}`);
    }
  }

  /**
   * Called when component is connected to DOM
   */
  async connectedCallback() {
    await this.init();
  }

  /**
   * Initialize component
   */
  async init() {
    try {
      this.log('⚙️ Initializing component...');

      // Load store configuration first
      await this.loadStoreConfig();
      this.log('📦 Store config loaded', this.currentStoreConfig);

      // Cache DOM elements
      this.cacheElements();

      // Load countries data BEFORE restoring preferences
      // This allows isCountryForCurrentStore() to check actual store mapping
      await this.loadCountries();

      // Restore saved preferences from localStorage (updates currentCountry/currentLocale)
      // Now isCountryForCurrentStore() can use this.countries data
      this.restorePreferences();

      this.log('✅ Preferences restored', {
        country: this.currentCountry,
        locale: this.currentLocale,
      });

      // Bind event listeners
      this.bindEvents();

      // Listen for browser back/forward navigation
      window.addEventListener('popstate', this._handlePopstate);

      // Listen for page restore from bfcache (browser back/forward cache)
      window.addEventListener('pageshow', this._handlePageshow);

      // Re-render countries list with correct current country (after restorePreferences)
      // This ensures the selected country checkmark is correct
      this.renderCountries();

      // Update UI after countries are loaded (now we have flags)
      this.updateTriggerUI();

      // Initialize tabs on first open (desktop only)
      this.details?.addEventListener('toggle', () => {
        if (this.details.open && !this.tabby && window.matchMedia('(min-width: 768px)').matches) {
          this.initTabs();
        }
      });
    } catch (error) {
      console.error('[CountryLocaleSwitcher] Initialization failed:', error);
    }
  }

  /**
   * Load store configuration and available locales
   */
  async loadStoreConfig() {
    try {
      const url = `${this.assetBaseUrl}/store-config.json`;
      const config = await this.safeFetch(url);

      // Cache config for later use
      this._storeConfigCache = config;

      // Store available locales for path manipulation
      this.availableLocales = config.locales || ['en', 'de', 'it', 'es', 'fr'];

      // Find store by shopifyDomain if storeId doesn't match
      if (this.storeId && config.stores[this.storeId]) {
        this.currentStoreConfig = { ...config.stores[this.storeId], id: this.storeId };
      } else {
        // Try to find by shopifyDomain
        const currentShopifyDomain = `${this.storeId}.myshopify.com`;

        for (const [storeKey, storeData] of Object.entries(config.stores)) {
          if (storeData.shopifyDomain === currentShopifyDomain) {
            // Add store ID to config object for validation
            this.currentStoreConfig = { ...storeData, id: storeKey };

            break;
          }
        }
      }

      // loaded available locales and current store config (debug logs removed)
    } catch (error) {
      console.error('[CountryLocaleSwitcher] Failed to load store config:', error);
      // Fallback to hardcoded locales
      this.availableLocales = ['en', 'de', 'it', 'es', 'fr'];

      // Try to infer store config from current domain
      this.inferCurrentStoreConfig();
    }
  }

  /**
   * Try to infer current store configuration from domain
   */
  inferCurrentStoreConfig() {
    try {
      const currentDomain = window.location.hostname;

      const fallbackConfig = {
        'inkposter.com': { defaultLocale: 'en' },
        'inkposter.eu': { defaultLocale: 'de' },
        'inkposter.co.uk': { defaultLocale: 'en' },
        'global.inkposter.com': { defaultLocale: 'en' },
      };

      this.currentStoreConfig = fallbackConfig[currentDomain];
    } catch (error) {
      console.error('[CountryLocaleSwitcher] Failed to infer store config:', error);
    }
  }

  /**
   * Cache frequently accessed elements
   */
  cacheElements() {
    this.details = this.querySelector('[data-country-details]');
    this.summary = this.querySelector('[data-country-summary]');
    this.countryList = this.querySelector('[data-country-list]');
    this.searchInput = this.querySelector('[data-country-search]');
  }

  /**
   * Restore saved preferences from localStorage or URL params
   * Security: validates all inputs before using them
   */
  restorePreferences() {
    try {
      this.log('🔍 Restoring preferences...');

      // PRIORITY 1: Check URL params (cross-domain transfer)
      const urlParams = new URLSearchParams(window.location.search);
      const urlCountry = urlParams.get('selected_country');
      const urlLocale = urlParams.get('selected_locale');

      // Store flag to prevent locale redirect (must be read BEFORE URL cleanup!)
      // This is critical for multi-instance scenarios where one instance may clean URL
      // before another instance calls checkAndRedirectToCorrectStore()
      this._hasSelectedLocaleParam = Boolean(urlLocale);

      this.log('📍 URL params', { urlCountry, urlLocale });

      let restoredFromUrl = false;
      let localeLocked = false;

      // Validate and restore country from URL
      // URL params come from cross-domain transfer (our own code), so ALWAYS trust them
      if (urlCountry && this.validateCountryCode(urlCountry)) {
        this.currentCountry = urlCountry;
        this.savePreference('country', urlCountry);
        restoredFromUrl = true;
        this.log('✅ Country restored from URL', { urlCountry });
      }

      // Validate and restore locale from URL
      // Check if current store supports the requested locale, fallback to default if not
      if (urlLocale && this.validateLocale(urlLocale)) {
        const storeSupportsLocale = this.currentStoreConfig?.availableLocales?.includes(urlLocale) ?? true;

        if (storeSupportsLocale) {
          this.currentLocale = urlLocale;
          this.savePreference('locale', urlLocale);
          this.log('✅ Locale restored from URL', { urlLocale });
        } else {
          // Fallback to store's default locale
          const fallbackLocale = this.currentStoreConfig?.defaultLocale || 'en';

          this.currentLocale = fallbackLocale;
          this.savePreference('locale', fallbackLocale);
          this.log('⚠️ URL locale not supported, using fallback', { urlLocale, fallbackLocale });
        }

        restoredFromUrl = true;
        localeLocked = true;
      }

      const pathLocale = this.getLocaleFromPath();

      if (!localeLocked && pathLocale && this.validateLocale(pathLocale)) {
        const storeSupportsLocale = this.currentStoreConfig?.availableLocales?.includes(pathLocale) ?? true;
        const fallbackLocale = this.currentStoreConfig?.defaultLocale || 'en';

        this.currentLocale = storeSupportsLocale ? pathLocale : fallbackLocale;
        this.savePreference('locale', this.currentLocale);
        this._hasExplicitPathLocale = storeSupportsLocale;
        localeLocked = true;
        this.log('✅ Locale restored from path', { pathLocale, resolvedLocale: this.currentLocale });
      }

      // PRIORITY 2: Check localStorage (same domain) - but validate against current store
      if (!restoredFromUrl) {
        const saved = localStorage.getItem(this.STORAGE_KEY);
        const ssrCountry = this.dataset.currentCountry;
        const ssrLocale = this.dataset.currentLocale;

        this.log('📦 localStorage check', {
          hasSaved: !!saved,
          ssrCountry,
          ssrLocale,
        });

        if (saved) {
          let parsedPrefs;

          try {
            parsedPrefs = JSON.parse(saved);
            this.log('📂 Parsed localStorage', parsedPrefs);
          } catch (parseError) {
            console.warn('[CountryLocaleSwitcher] Invalid JSON in localStorage, using SSR:', parseError);
            this.currentCountry = ssrCountry;
            this.currentLocale = localeLocked ? this.currentLocale : ssrLocale;

            return;
          }

          // Validate parsed data is an object
          if (!parsedPrefs || typeof parsedPrefs !== 'object') {
            console.warn('[CountryLocaleSwitcher] Invalid preferences format, using SSR');
            this.currentCountry = ssrCountry;
            this.currentLocale = localeLocked ? this.currentLocale : ssrLocale;

            return;
          }

          const { country, locale } = parsedPrefs;

          // Validate country before using
          if (country && this.validateCountryCode(country)) {
            // Check if localStorage country belongs to current store
            const isCountryForCurrentStore = this.isCountryForCurrentStore(country);

            if (isCountryForCurrentStore) {
              this.currentCountry = country;
              this.log('✅ Using localStorage country', { country });

              // On store-global we still need IP-based routing to EU for EU countries
              // (e.g., VPN Italy should redirect to store-eu /it/).
              // Otherwise, global can get "stuck" on an old saved country.
              const disableAutoRedirectRaw = this.dataset.disableAutoRedirect;

              const disableAutoRedirect =
                disableAutoRedirectRaw === undefined
                  ? true
                  : String(disableAutoRedirectRaw).trim().toLowerCase() === 'true';

              if (this.currentStoreConfig?.id === 'store-global' && !disableAutoRedirect) {
                this.log('🔄 store-global: running IP redirect check even with saved country', {
                  savedCountry: country,
                  ssrCountry,
                });
                this.checkAndRedirectToCorrectStore(ssrCountry);
              }
            } else {
              // localStorage has country from different store.
              // If auto-redirect is disabled, keep the saved country for UI (no redirect),
              // otherwise trigger IP-based redirect.
              const disableAutoRedirectRaw = this.dataset.disableAutoRedirect;

              const disableAutoRedirect =
                disableAutoRedirectRaw === undefined
                  ? true
                  : String(disableAutoRedirectRaw).trim().toLowerCase() === 'true';

              if (disableAutoRedirect) {
                this.currentCountry = country;
                this.log('🛑 Auto-redirect disabled, keeping localStorage country for UI', { country });
              } else {
                this.log('🔄 localStorage country mismatch, checking IP geolocation');
                this.checkAndRedirectToCorrectStore(ssrCountry);

                return; // Stop processing, redirect check will happen
              }
            }
          } else {
            // Invalid or missing country in localStorage - fallback to SSR and still validate locale
            // NOTE: Older preferences might only contain locale, so don't early-return here.
            this.log('⚠️ Invalid/missing localStorage country, falling back to SSR country');
            this.currentCountry = ssrCountry;
            this.checkAndRedirectToCorrectStore(ssrCountry);
          }

          if (!localeLocked) {
            // Validate locale before using
            // Check if current store supports the locale from localStorage
            if (locale && this.validateLocale(locale)) {
              const storeSupportsLocale = this.currentStoreConfig?.availableLocales?.includes(locale) ?? true;

              if (storeSupportsLocale) {
                this.currentLocale = locale;
              } else {
                // localStorage has locale not supported by current store, use SSR or default
                const fallbackLocale = this.currentStoreConfig?.defaultLocale || ssrLocale || 'en';

                this.currentLocale = fallbackLocale;
                // Update localStorage to match current store
                this.savePreference('locale', fallbackLocale);
              }
            } else {
              // Invalid or missing locale, use SSR
              this.currentLocale = ssrLocale;
            }
          } else {
            this.savePreference('locale', this.currentLocale);
          }
        } else {
          // No localStorage - first visit!
          // PRIORITY 3: Use Shopify Geolocation (SSR) for country
          this.currentCountry = ssrCountry;
          this.log('🌍 First visit - using Shopify Geolocation', { ssrCountry });

          // PRIORITY 4: Try browser locale detection for language
          if (!localeLocked) {
            const browserLocale = this.detectBrowserLocale();

            if (browserLocale && browserLocale !== ssrLocale) {
              this.log('🗣️ Browser locale detected', { browserLocale, ssrLocale });
              this.currentLocale = browserLocale;
              // Save detected locale so we don't detect again
              this.savePreference('locale', browserLocale);
            } else {
              this.currentLocale = ssrLocale;
              this.log('📄 Using SSR locale', { ssrLocale });
            }
          } else {
            this.currentLocale = this.currentLocale || ssrLocale;
          }

          // PRIORITY 5: Check if user should be redirected to correct store
          // Only on first visit (no localStorage) and if SSR country doesn't belong to current store
          this.checkAndRedirectToCorrectStore(ssrCountry);
        }
      }

      // Clean URL params after reading (prevent replay attacks)
      // Defer cleanup to allow all component instances to read URL params first
      if (urlCountry || urlLocale) {
        // Use static flag to ensure cleanup happens only once after all instances initialize
        if (!CountryLocaleSwitcher._urlCleanupScheduled) {
          CountryLocaleSwitcher._urlCleanupScheduled = true;

          // Wait for next tick to ensure all component instances have read URL params
          setTimeout(() => {
            const cleanUrl = new URL(window.location.href);

            cleanUrl.searchParams.delete('selected_country');
            cleanUrl.searchParams.delete('selected_locale');
            // Use a same-origin relative URL to avoid SecurityError in restricted environments
            // (e.g., JSDOM when window.location is mocked).
            window.history.replaceState({}, '', `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`);

            // Reset flag for next navigation
            CountryLocaleSwitcher._urlCleanupScheduled = false;
          }, 100); // Small delay to allow multiple instances to read params
        }
      }
    } catch (error) {
      console.error('[CountryLocaleSwitcher] Failed to restore preferences:', error);
    }
  }

  /**
   * Validate country code (ISO 3166-1 alpha-2)
   * Security: prevents injection attacks
   * @param {string} code - Country code to validate
   * @returns {boolean} True if valid
   */
  validateCountryCode(code) {
    // Must be exactly 2 uppercase letters
    if (typeof code !== 'string' || !/^[A-Z]{2}$/.test(code)) {
      console.warn('[CountryLocaleSwitcher] Invalid country code:', code);

      return false;
    }

    return true;
  }

  /**
   * Normalize country code input to ISO 3166-1 alpha-2 uppercase.
   * @param {unknown} value - Any value that might represent a country code
   * @returns {string} Uppercased country code, or empty string
   */
  normalizeCountryCode(value) {
    if (typeof value !== 'string') return '';

    return value.trim().toUpperCase();
  }

  /**
   * Resolve the URL to the flags sprite asset.
   * Note: We intentionally do NOT inline the sprite because inline_asset_content
   * can be truncated/limited for large SVG files.
   * @returns {string} Absolute or relative URL to flags-sprite.svg, or empty string
   */
  getFlagsSpriteUrl() {
    const fromDom = document.querySelector('[data-flags-sprite]')?.dataset?.flagsSpriteUrl;

    if (typeof fromDom === 'string' && fromDom.trim()) {
      return fromDom;
    }

    const base = typeof this.assetBaseUrl === 'string' ? this.assetBaseUrl.trim() : '';

    if (!base) return '/assets/flags-sprite.svg';

    const normalizedBase = base.endsWith('/') ? base : `${base}/`;

    return `${normalizedBase}flags-sprite.svg`;
  }

  /**
   * Resolve fallback flag country code for the current store.
   * Policy: when selected/SSR country is unknown/unavailable, show store flag instead.
   * store-us -> US, store-eu -> DE, store-global -> AU
   * @returns {string} ISO alpha-2 country code
   */
  getStoreFlagCountryCode() {
    const storeId = this.currentStoreConfig?.id;

    if (storeId === 'store-eu') return 'DE';

    if (storeId === 'store-global') return 'AU';

    if (storeId === 'store-uk') return 'GB';

    return 'US';
  }

  /**
   * Build sprite symbol id for a given country code.
   * @param {string} countryCode - ISO alpha-2 country code
   * @returns {string} Symbol id (without #), or empty string
   */
  getFlagSymbolId(countryCode) {
    const normalized = this.normalizeCountryCode(countryCode);

    if (!this.validateCountryCode(normalized)) return '';

    return `flag-${normalized.toLowerCase()}`;
  }

  /**
   * Check if a sprite symbol exists in DOM for the given country.
   * @param {string} countryCode - ISO alpha-2 country code
   * @returns {boolean} True if symbol is present
   */
  hasFlagSymbol(countryCode) {
    const symbolId = this.getFlagSymbolId(countryCode);

    const spriteUrl = this.getFlagsSpriteUrl();

    if (!symbolId || !spriteUrl) return false;

    return true;
  }

  /**
   * Get SVG markup for a flag using the inlined sprite.
   * Security: only uses validated ISO codes to build the href.
   * @param {string} countryCode - ISO alpha-2 country code
   * @returns {string} SVG markup or empty string
   */
  getFlagSvgMarkup(countryCode) {
    const symbolId = this.getFlagSymbolId(countryCode);

    const spriteUrl = this.getFlagsSpriteUrl();

    if (!symbolId || !spriteUrl) return '';

    return `
      <svg class="block h-full w-full" aria-hidden="true" focusable="false" viewBox="0 0 36 36">
        <use href="${spriteUrl}#${symbolId}"></use>
      </svg>
    `.trim();
  }

  /**
   * Get inner markup for a flag.
   * Policy: no emoji rendering in UI. If sprite URL is not available, return empty.
   * @param {string} countryCode - ISO alpha-2 country code
   * @returns {string} HTML markup
   */
  getFlagInnerMarkup(countryCode) {
    const normalized = this.normalizeCountryCode(countryCode);

    if (!this.hasFlagSymbol(normalized)) return '';

    return this.getFlagSvgMarkup(normalized);
  }

  /**
   * Validate locale code
   * Security: whitelist validation against available locales
   * @param {string} locale - Locale code to validate
   * @returns {boolean} True if valid
   */
  validateLocale(locale) {
    if (typeof locale !== 'string') {
      console.warn('[CountryLocaleSwitcher] Invalid locale:', locale);

      return false;
    }

    // Primary source: locales from store-config.json (fast path)
    const validLocales = this.availableLocales.length > 0 ? this.availableLocales : ['en', 'de', 'it', 'es', 'fr'];

    if (validLocales.includes(locale)) {
      return true;
    }

    // Fallback source: locales rendered in the switcher UI.
    // This prevents language switching from breaking when config.locales is incomplete.
    const hasLocaleInDom = Array.from(this.querySelectorAll('[data-locale-code]')).some(
      (el) => el?.dataset?.localeCode === locale
    );

    if (hasLocaleInDom) {
      return true;
    }

    console.warn('[CountryLocaleSwitcher] Invalid locale:', locale);

    return false;
  }

  /**
   * Detect user's preferred locale from browser settings
   * Uses navigator.languages to find best match with available locales
   * Only used on first visit when no locale preference is saved
   * @returns {string|null} Locale code or null if no match found
   */
  detectBrowserLocale() {
    try {
      // Get browser languages in order of preference
      const browserLanguages = navigator.languages || [navigator.language];

      this.log('🌐 Browser languages detected', browserLanguages);

      for (const lang of browserLanguages) {
        // Extract language code (e.g., "en-US" -> "en", "de-DE" -> "de")
        const localeCode = lang.split('-')[0].toLowerCase();

        // Check if current store supports this locale
        const storeSupportsLocale = this.currentStoreConfig?.availableLocales?.includes(localeCode) ?? false;

        if (storeSupportsLocale && this.validateLocale(localeCode)) {
          this.log('✅ Browser locale matched', { localeCode, storeSupportsLocale });

          return localeCode;
        }
      }

      this.log('⚠️ No browser locale match found in store locales', {
        browserLanguages,
        storeLocales: this.currentStoreConfig?.availableLocales,
      });

      return null;
    } catch (error) {
      console.warn('[CountryLocaleSwitcher] Browser locale detection failed:', error);

      return null;
    }
  }

  /**
   * Select appropriate locale based on country and store
   * For store-eu: Germany/Austria/Switzerland → German, Italy → Italian, Spain → Spanish
   * For other EU countries → English
   * For other stores: tries browser locale, then falls back to store default
   * @param {string} countryCode - ISO country code
   * @param {Object} storeConfig - Target store configuration
   * @returns {string} Locale code
   */
  selectLocaleForCountry(countryCode, storeConfig) {
    this.log('🔍 selectLocaleForCountry called', {
      countryCode,
      storeId: storeConfig?.id,
      availableLocales: storeConfig?.availableLocales,
      defaultLocale: storeConfig?.defaultLocale,
    });

    // Validate storeConfig parameter
    if (!storeConfig || typeof storeConfig !== 'object') {
      this.log('⚠️ storeConfig is null/undefined or invalid, returning null');

      return null;
    }

    // Ensure required properties exist
    if (!storeConfig.id || !storeConfig.defaultLocale) {
      this.log('⚠️ storeConfig missing required properties (id or defaultLocale)', storeConfig);

      return storeConfig.defaultLocale || 'en';
    }

    // Country-to-locale mapping for store-eu
    if (storeConfig.id === 'store-eu') {
      const countryLocaleMap = {
        DE: 'de', // Germany → German
        AT: 'de', // Austria → German
        CH: 'de', // Switzerland → German (primarily German-speaking)
        IT: 'it', // Italy → Italian
        ES: 'es', // Spain → Spanish
        FR: 'fr', // France → French
      };

      const mappedLocale = countryLocaleMap[countryCode];

      if (mappedLocale && storeConfig.availableLocales?.includes(mappedLocale)) {
        this.log('✅ Country-based locale selected', { countryCode, mappedLocale });

        return mappedLocale;
      }

      // For other EU countries, prefer English, then store default locale
      const { availableLocales = [], defaultLocale } = storeConfig || {};

      let fallbackLocale = null;

      // 1) Use English if available (unmapped EU countries should get English)
      if (availableLocales.includes('en')) {
        fallbackLocale = 'en';
      } else if (defaultLocale && availableLocales.includes(defaultLocale)) {
        // 2) Otherwise, use defaultLocale if present and available
        fallbackLocale = defaultLocale;
      } else if (availableLocales.length > 0) {
        // 3) As a last resort, use the first available locale
        fallbackLocale = availableLocales[0];
      } else {
        // 4) Absolute fallback: keep previous behavior and use 'en'
        fallbackLocale = 'en';
      }

      this.log('ℹ️ Unmapped EU country, using fallback locale', {
        countryCode,
        fallbackLocale,
        defaultLocale,
        availableLocales,
      });

      return fallbackLocale;
    }

    // For other stores: try browser locale detection
    const browserLocale = this.detectBrowserLocale();

    this.log('ℹ️ Non-EU store, using browser locale or default', {
      storeId: storeConfig?.id,
      browserLocale,
      defaultLocale: storeConfig?.defaultLocale,
    });

    return browserLocale || storeConfig.defaultLocale || 'en';
  }

  /**
   * Check if user has previously made a manual selection
   * @returns {boolean} True if user has saved preferences in localStorage
   */
  hasUserPreferences() {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEY);

      return saved !== null;
    } catch {
      return false;
    }
  }

  /**
   * Detect user's country from IP address using free geolocation API
   * Uses ipapi.co service (1000 requests/day free, no API key needed)
   * @returns {Promise<string|null>} ISO country code or null
   */
  async detectCountryFromIP() {
    try {
      // Check if we already detected in this session (avoid repeated API calls)
      const cached = sessionStorage.getItem('inkposter_ip_country');

      if (cached) {
        this.log('📍 Using cached IP country', { cached });

        return cached;
      }

      this.log('🌐 Fetching country from IP...');

      const response = await fetch('https://ipapi.co/json/', {
        signal: AbortSignal.timeout(1500), // 1.5s timeout
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      const countryCode = data.country_code;

      this.log('📍 IP Geolocation result', {
        countryCode,
        countryName: data.country_name,
        city: data.city,
      });

      if (countryCode && this.validateCountryCode(countryCode)) {
        // Cache in sessionStorage to avoid repeated API calls during session
        sessionStorage.setItem('inkposter_ip_country', countryCode);

        return countryCode;
      }

      return null;
    } catch (error) {
      console.warn('[CountryLocaleSwitcher] IP geolocation failed:', error);

      return null;
    }
  }

  /**
   * Check and redirect user to correct store based on IP geolocation
   * Only runs on first visit (no localStorage) when detected country doesn't match current store
   * @param {string} ssrCountry - Country code from Shopify (fallback)
   */
  async checkAndRedirectToCorrectStore(ssrCountry) {
    try {
      // CRITICAL: Check if user was just redirected with selected_locale URL parameter FIRST
      // Use instance property set in restorePreferences() instead of reading URL directly
      // because URL params may already be cleaned by another component instance
      if (this._hasSelectedLocaleParam) {
        this.log('⏭️ Skipping all redirect logic - user has selected_locale URL param');

        return;
      }

      const isLocalDev =
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        document.querySelector('script[src*="localhost:5173"]');

      const disableAutoRedirectRaw = this.dataset.disableAutoRedirect;

      const disableAutoRedirect =
        disableAutoRedirectRaw === undefined ? true : String(disableAutoRedirectRaw).trim().toLowerCase() === 'true';

      const designModeRaw = this.dataset.designMode;

      const isLiquidDesignMode =
        designModeRaw === undefined ? false : String(designModeRaw).trim().toLowerCase() === 'true';

      const isShopifyDesignMode = globalThis.Shopify?.designMode === true;
      const referrer = globalThis.document?.referrer || '';
      const isShopifyAdminReferrer = referrer.includes('admin.shopify.com');

      if (disableAutoRedirect || isLiquidDesignMode || isShopifyDesignMode || isShopifyAdminReferrer || isLocalDev) {
        this.log('🛑 Auto-redirect disabled, skipping redirect check', {
          disableAutoRedirect,
          isLiquidDesignMode,
          isShopifyDesignMode,
          isShopifyAdminReferrer,
        });

        return;
      }

      const shouldSkipAutoRedirect = (candidateUrl) => {
        try {
          if (!candidateUrl || typeof candidateUrl !== 'string') return false;

          const key = 'inkposter_auto_redirect_guard';
          const now = Date.now();
          const raw = sessionStorage.getItem(key);
          const last = raw ? JSON.parse(raw) : null;
          const ttlMs = 15000;

          if (!last || !last.url || typeof last.timestamp !== 'number') {
            return false;
          }

          const isExpired = now - last.timestamp > ttlMs;

          if (isExpired) {
            sessionStorage.removeItem(key);

            return false;
          }

          return last.url === candidateUrl;
        } catch (error) {
          this.log('⚠️ Auto-redirect guard failed', { error });

          return false;
        }
      };

      const markAutoRedirect = (candidateUrl) => {
        try {
          if (!candidateUrl || typeof candidateUrl !== 'string') return;

          const key = 'inkposter_auto_redirect_guard';
          const payload = { url: candidateUrl, timestamp: Date.now() };

          sessionStorage.setItem(key, JSON.stringify(payload));
        } catch (error) {
          this.log('⚠️ Auto-redirect guard write failed', { error });
        }
      };

      // Wait for countries data to be loaded
      if (!this.countries || this.countries.length === 0) {
        this.log('⏳ Countries not loaded yet, skipping redirect check');

        return;
      }

      // IMPORTANT: Use IP geolocation instead of SSR country
      // Shopify's localization.country returns selected country, not IP-based
      const ipCountry = await this.detectCountryFromIP();
      const detectedCountry = ipCountry || ssrCountry;

      this.log('🎯 Detected country for redirect check', {
        ipCountry,
        ssrCountry,
        usingCountry: detectedCountry,
      });

      // Check if detected country belongs to current store
      const belongsToCurrentStore = this.isCountryForCurrentStore(detectedCountry);

      this.log('🔍 Checking if redirect needed', {
        detectedCountry,
        belongsToCurrentStore,
        currentStoreId: this.currentStoreConfig?.id,
      });

      if (belongsToCurrentStore) {
        this.log('✅ User is on correct store');

        // Update current country to IP-detected one (for UI)
        if (ipCountry && ipCountry !== this.currentCountry) {
          this.currentCountry = ipCountry;
          this.savePreference('country', ipCountry);
          this.updateTriggerUI();
        }

        // ONLY redirect to locale-specific URL for store-eu with country-based locale mapping
        // For other stores, don't force locale redirect unless user explicitly selected country
        if (this.currentStoreConfig?.id === 'store-eu') {
          if (this._hasExplicitPathLocale) {
            this.log('⏭️ Skipping EU locale redirect - explicit locale in path');

            return;
          }

          // Check if user is on correct locale for their country
          const expectedLocale = this.selectLocaleForCountry(detectedCountry, this.currentStoreConfig);
          const currentPathLocale = this.getLocaleFromPath();
          const currentLocale = currentPathLocale || this.currentStoreConfig?.defaultLocale || 'en';

          this.log('🔍 EU store locale check', {
            detectedCountry,
            expectedLocale,
            currentLocale,
            currentPathLocale,
            defaultLocale: this.currentStoreConfig?.defaultLocale,
          });

          // If expected locale differs from current, redirect to correct locale
          if (expectedLocale !== currentLocale) {
            this.log('🔄 Redirecting to correct locale for country', {
              from: currentLocale,
              to: expectedLocale,
              country: detectedCountry,
            });

            const localeRedirectUrl = this.buildRedirectUrl(this.currentStoreConfig, expectedLocale);

            if (shouldSkipAutoRedirect(localeRedirectUrl)) {
              this.log('⏭️ Skipping auto-redirect loop guard', { localeRedirectUrl });

              return;
            }

            markAutoRedirect(localeRedirectUrl);
            this.log('➡️ Locale redirect', { redirectUrl: localeRedirectUrl, expectedLocale });
            window.location.href = localeRedirectUrl;

            return;
          }
        }

        this.log('✅ User is on correct store and locale, no redirect needed');

        return;
      }

      // User should be on a different store - find correct one
      const country = this.countries.find((c) => c.code === detectedCountry);

      if (!country) {
        this.log('⚠️ Country not found in data, skipping redirect', { detectedCountry });

        return;
      }

      this.log('🚀 Auto-redirect triggered!', {
        detectedCountry,
        countryName: country.name,
        targetStore: country.store,
        currentStore: this.currentStoreConfig?.id,
      });

      // Get target store config
      const storeConfig = await this.getStoreConfig(country.store);

      if (!storeConfig) {
        this.log('⚠️ Target store config not found, skipping redirect');

        return;
      }

      // IMPORTANT: Update currentCountry BEFORE building redirect URL
      // This ensures selected_country param contains the IP-detected country
      this.currentCountry = detectedCountry;

      // Save country preference before redirect (so it persists on target store)
      this.savePreference('country', detectedCountry);

      // Detect browser locale for target store
      // Check if target store supports browser locale
      let browserLocale = this.detectBrowserLocale();

      // Verify browser locale is supported by TARGET store
      if (browserLocale && storeConfig.availableLocales) {
        if (!storeConfig.availableLocales.includes(browserLocale)) {
          this.log('⚠️ Browser locale not supported by target store', {
            browserLocale,
            targetStoreLocales: storeConfig.availableLocales,
          });
          browserLocale = null; // Will fallback to store default
        }
      }

      // Use country-based locale selection for better UX
      // For store-eu: DE/AT/CH → de, IT → it, ES → es, others → en
      const targetLocale = this.selectLocaleForCountry(detectedCountry, storeConfig);

      this.log('🎯 Redirect params', {
        country: this.currentCountry,
        locale: targetLocale,
        browserLocale,
        storeDefaultLocale: storeConfig.defaultLocale,
        countryBasedLocale: targetLocale,
      });

      // Build redirect URL with country and locale params
      const redirectUrl = this.buildRedirectUrl(storeConfig, targetLocale);

      if (shouldSkipAutoRedirect(redirectUrl)) {
        this.log('⏭️ Skipping auto-redirect loop guard', { redirectUrl });

        return;
      }

      markAutoRedirect(redirectUrl);
      this.log('➡️ Redirecting to', { redirectUrl, targetLocale });

      // Redirect to correct store
      window.location.href = redirectUrl;
    } catch (error) {
      console.error('[CountryLocaleSwitcher] Auto-redirect failed:', error);
      // Don't throw - allow user to stay on current store
    }
  }

  /**
   * Check if country belongs to current store
   * Prevents showing wrong country after browser back navigation
   * Uses actual countries data to validate store mapping
   * @param {string} countryCode - Country code to check
   * @returns {boolean} True if country should be used for current store
   */
  isCountryForCurrentStore(countryCode) {
    // If countries not loaded yet, use simplified logic
    if (!this.countries || this.countries.length === 0) {
      const { hostname } = window.location;

      // Fallback: simple hostname-based validation
      if (countryCode === 'US' && hostname !== 'inkposter.com') {
        return false;
      }

      if (hostname === 'inkposter.com') {
        return countryCode === 'US';
      }

      // For EU/Global, accept any non-US country as fallback
      return countryCode !== 'US';
    }

    // Find country in loaded data
    const country = this.countries.find((c) => c.code === countryCode);

    if (!country) {
      console.warn('[CountryLocaleSwitcher] Country not found:', countryCode);

      return false;
    }

    // Get current store ID - MUST use currentStoreConfig.id (store-eu, store-us, store-global)
    // NOT this.storeId which contains Shopify domain (inkposter-de-production.myshopify.com)
    const currentStoreId = this.currentStoreConfig?.id;

    if (!currentStoreId) {
      console.warn('[CountryLocaleSwitcher] No store ID available for validation, accepting country:', countryCode);

      return true; // Accept if store config not loaded
    }

    // Check if country's store matches current store
    const belongsToCurrentStore = country.store === currentStoreId;

    if (!belongsToCurrentStore) {
    }

    return belongsToCurrentStore;
  }

  /**
   * Update trigger button UI with current country flag and locale
   */
  updateTriggerUI() {
    const flagEls = this.querySelectorAll('[data-current-flag]');
    const localeEls = this.querySelectorAll('[data-current-locale]');
    const countryEls = this.querySelectorAll('[data-current-country]');

    if (localeEls.length > 0) {
      localeEls.forEach((localeEl) => {
        localeEl.textContent = this.currentLocale.toUpperCase();
      });
    }

    if (flagEls && this.countries) {
      const country = this.currentCountry ? this.countries.find((c) => c.code === this.currentCountry) : null;
      const fallbackStoreCode = this.getStoreFlagCountryCode();
      const fallbackCountry = fallbackStoreCode ? this.countries.find((c) => c.code === fallbackStoreCode) : null;
      const normalizedCountryCode = country ? this.normalizeCountryCode(country.code) : '';

      flagEls.forEach((flagEl) => {
        const codeForFlag = this.hasFlagSymbol(normalizedCountryCode) ? normalizedCountryCode : fallbackStoreCode;

        if (this.hasFlagSymbol(codeForFlag)) {
          const spriteUrl = this.getFlagsSpriteUrl();
          const desiredSymbolId = this.getFlagSymbolId(codeForFlag);
          const desiredHref = `${spriteUrl}#${desiredSymbolId}`;
          const existingUse = flagEl.querySelector('use');

          if (!existingUse || existingUse.getAttribute('href') !== desiredHref) {
            flagEl.innerHTML = this.getFlagSvgMarkup(codeForFlag);
          }

          return;
        }

        // No emoji fallback - clear flag if sprite is unavailable.
        flagEl.innerHTML = '';
      });

      // Update country name (mobile UI)
      if (countryEls.length > 0) {
        const nameForUi = country?.name || fallbackCountry?.name;

        if (nameForUi) {
          countryEls.forEach((countryEl) => {
            countryEl.textContent = nameForUi;
          });
        }
      }
    }
  }

  /**
   * Initialize Tabby tabs (desktop only)
   * Called lazily on first details open
   */
  async initTabs() {
    // Check if tabs element exists
    const tabsElement = this.querySelector('[data-tabs]');

    if (!tabsElement) {
      console.warn('[CountryLocaleSwitcher] Tabs element not found, skipping Tabby init');

      return null;
    }

    try {
      const { default: Tabby } = await import('tabbyjs');

      this.tabby = new Tabby('[data-tabs]', {
        default: '[data-tab="language"]',
      });

      return this.tabby;
    } catch (error) {
      console.error('[CountryLocaleSwitcher] Failed to initialize Tabby:', error);

      return null;
    }
  }

  /**
   * Bind all event listeners
   */
  bindEvents() {
    // Search input with debounce
    this.searchInput?.addEventListener('input', (e) => {
      this.debouncedSearch(e.target.value);
    });

    // Locale buttons (bind after render or use delegation)
    this.bindLocaleClicks();
  }

  /**
   * Debounced search to improve performance
   */
  debouncedSearch(query) {
    // Clear previous timer
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }

    // Set new timer
    this.searchDebounceTimer = setTimeout(() => {
      this.filterCountries(query);
    }, this.SEARCH_DEBOUNCE_MS);
  }

  /**
   * Bind click events to locale buttons
   */
  bindLocaleClicks() {
    this.querySelectorAll('[data-locale-code]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const locale = e.currentTarget.dataset.localeCode;

        this.selectLocale(locale);
      });
    });
  }

  /**
   * Cleanup - remove all event listeners and clear caches
   */
  disconnectedCallback() {
    this.tabby?.destroy();

    // Remove window event listeners to prevent memory leaks
    window.removeEventListener('popstate', this._handlePopstate);
    window.removeEventListener('pageshow', this._handlePageshow);

    // Clear search cache and timers
    this.searchCache.clear();

    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
  }

  /**
   * Handle pageshow event for bfcache restore
   * @param {PageTransitionEvent} event - The pageshow event
   */
  _onPageShow(event) {
    if (event.persisted) {
      // Page was restored from cache, sync state
      this.handleBrowserNavigation();
    }
  }

  /**
   * Safe fetch with response.ok check
   * @param {string} url - URL to fetch
   * @returns {Promise<any>} Parsed JSON response
   * @throws {Error} If response is not ok
   */
  async safeFetch(url) {
    const cache = CountryLocaleSwitcher._jsonFetchCache;

    if (cache.has(url)) {
      return cache.get(url);
    }

    const promise = (async () => {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return response.json();
    })();

    cache.set(url, promise);

    try {
      return await promise;
    } catch (error) {
      cache.delete(url);
      throw error;
    }
  }

  /**
   * Load countries data with current locale translations
   */
  async loadCountries() {
    try {
      const [indexData, translations, currencies] = await Promise.all([
        this.safeFetch(`${this.assetBaseUrl}/countries-index.json`),
        this.safeFetch(`${this.assetBaseUrl}/countries-translations-${this.currentLocale}.json`),
        this.safeFetch(`${this.assetBaseUrl}/currencies.json`),
      ]);

      // Merge data
      this.countries = this.mergeCountriesData(indexData, translations, currencies);
    } catch (error) {
      console.error('[CountryLocaleSwitcher] Failed to load countries:', error);

      // Fallback: try to load with English translations
      if (this.currentLocale !== 'en') {
        await this.fallbackLoadCountries();
      }
    }
  }

  /**
   * Fallback loading with English translations
   */
  async fallbackLoadCountries() {
    try {
      const [indexData, translations, currencies] = await Promise.all([
        this.safeFetch(`${this.assetBaseUrl}/countries-index.json`),
        this.safeFetch(`${this.assetBaseUrl}/countries-translations-en.json`),
        this.safeFetch(`${this.assetBaseUrl}/currencies.json`),
      ]);

      // Merge data
      this.countries = this.mergeCountriesData(indexData, translations, currencies);
    } catch (fallbackError) {
      console.error('[CountryLocaleSwitcher] Fallback loading also failed:', fallbackError);
      // Component will work without countries list
    }
  }

  /**
   * Merge countries data with translations and currencies
   * Filters out countries belonging to disabled stores
   */
  mergeCountriesData(index, translations, currencies) {
    const merged = [];

    for (const [code, meta] of Object.entries(index)) {
      const rawName = translations && typeof translations === 'object' ? translations[code] : undefined;
      const name = typeof rawName === 'string' && rawName.trim().length > 0 ? rawName : code;
      const { store, flag } = meta;

      // Skip countries from disabled stores
      if (!this.isStoreEnabled(store)) {
        continue;
      }

      const currency = this.getStoreCurrency(store, currencies);

      merged.push({
        code,
        name,
        flag,
        store,
        currency: {
          code: currency.code,
          symbol: currency.symbol,
        },
      });
    }

    return merged;
  }

  /**
   * Check if a store is enabled
   * @param {string} storeId - Store ID (e.g., 'store-us', 'store-global')
   * @returns {boolean} True if store is enabled (or if enabled flag is not set)
   */
  isStoreEnabled(storeId) {
    const storeConfig = this._storeConfigCache?.stores?.[storeId];

    // If no config found, assume enabled (backwards compatibility)
    if (!storeConfig) {
      return true;
    }

    // If enabled flag is not set, assume enabled (backwards compatibility)
    // Only return false if explicitly set to false
    return storeConfig.enabled !== false;
  }

  /**
   * Get currency for store with fallback
   * @param {string} storeId - Store ID
   * @param {Object} currencies - Currencies data
   * @returns {Object} Currency object with code and symbol
   */
  getStoreCurrency(storeId, currencies) {
    const currencyCode = currencies.storeCurrencies?.[storeId];
    const currency = currencies.currencies?.[currencyCode];

    // Fallback to USD if currency not found
    return currency || { code: 'USD', symbol: '$' };
  }

  /**
   * Render countries list
   */
  renderCountries() {
    if (!this.countryList || !this.countries) return;

    // Sort countries with current country first, then alphabetically
    const sortedCountries = [...this.countries].sort((a, b) => {
      // Current country always first
      if (a.code === this.currentCountry) return -1;

      if (b.code === this.currentCountry) return 1;

      // Others alphabetically
      const nameA = typeof a.name === 'string' ? a.name : '';
      const nameB = typeof b.name === 'string' ? b.name : '';

      return nameA.localeCompare(nameB);
    });

    const html = sortedCountries.map((country) => this.renderCountryItem(country)).join('');

    this.countryList.innerHTML = html;

    // Bind click events
    this.bindCountryClicks();
  }

  /**
   * Render single country item
   * Security: all dynamic content is escaped to prevent XSS
   * @param {Object} country - Country data object
   * @param {string} highlightQuery - Optional search query for highlighting
   * @returns {string} HTML string for country list item
   */
  renderCountryItem(country, highlightQuery = '') {
    const isCurrent = country.code === this.currentCountry;

    const countryName = typeof country.name === 'string' ? country.name : '';

    // Security: escape all dynamic content from JSON
    const nameHtml = highlightQuery ? this.highlightText(countryName, highlightQuery) : this.escapeHtml(countryName);

    // Validate and escape country code (should be 2 uppercase letters)
    const safeCode = this.validateCountryCode(country.code) ? country.code : '';

    // Escape currency info
    const currencyCode = this.escapeHtml(country.currency?.code || '');
    const currencySymbol = this.escapeHtml(country.currency?.symbol || '');
    const currencyInfo = `${currencyCode} ${currencySymbol}`;

    const flagInner = this.getFlagInnerMarkup(safeCode);
    const flagSpan = `<span class="mr-1 h-5 w-5 shrink-0" aria-hidden="true">${flagInner}</span>`;

    const checkIcon = `
      <span class="h-5 w-5 shrink-0">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          role="img"
          aria-label="Icon Check"
          width="20"
          height="20"
          viewBox="0 0 16 16"
        >
          <desc>Icon Check</desc>
          <path
            d="M13.3334 4L6.00008 11.3333L2.66675 8"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </span>
    `;

    if (isCurrent) {
      return `
        <li class="border-b border-gray-300">
          <div
            class="flex items-baseline justify-between px-4 py-3 text-left"
            aria-current="true"
          >
            <span class="flex items-start gap-1 text-wrap">
              ${flagSpan}
              <span>${nameHtml}</span>
              <span class="shrink-0 min-w-15 text-right">${currencyInfo}</span>
            </span>

            ${checkIcon}
          </div>
        </li>
      `;
    }

    return `
      <li class="border-b border-gray-300">
        <button
          type="button"
          class="
            flex
            items-center
            justify-between
            w-full
            text-left
            px-4
            py-3
            cursor-pointer
            hover:text-brand-tertiary
            focus-visible:text-brand-tertiary
          "
          data-country-code="${safeCode}"
        >
          <span class="flex items-baseline justify-between">
            <span class="flex items-start gap-1 text-wrap">
              ${flagSpan}
              <span>${nameHtml}</span>
              <span class="shrink-0 min-w-15 text-right">${currencyInfo}</span>
            </span>
          </span>
        </button>
      </li>
    `;
  }

  /**
   * Bind click events to country buttons using event delegation
   * More efficient than adding listeners to each button
   */
  bindCountryClicks() {
    // Remove old delegation listener if exists
    if (this._countryClickHandler) {
      this.countryList?.removeEventListener('click', this._countryClickHandler);
    }

    // Create bound handler for delegation
    this._countryClickHandler = (e) => {
      const button = e.target.closest('[data-country-code]');

      if (button) {
        const code = button.dataset.countryCode;

        this.selectCountry(code);
      }
    };

    // Add single listener to container
    this.countryList?.addEventListener('click', this._countryClickHandler);
  }

  /**
   * Filter countries by search query with caching and limiting
   */
  filterCountries(query) {
    if (!this.countries) return;

    const lowerQuery = query.toLowerCase().trim();

    // Show all if empty
    if (!lowerQuery) {
      this.showAllCountries();

      return;
    }

    // Check cache first
    const cacheKey = lowerQuery;

    if (this.searchCache.has(cacheKey)) {
      const cachedResults = this.searchCache.get(cacheKey);

      this.renderFilteredCountries(cachedResults, lowerQuery);

      return;
    }

    // Filter countries by name or ISO code with early exit for performance
    const filteredCountries = [];

    for (const country of this.countries) {
      if (filteredCountries.length >= this.MAX_RESULTS) break;

      const countryName = typeof country.name === 'string' ? country.name : '';
      const nameMatch = countryName.toLowerCase().includes(lowerQuery);
      const codeMatch = country.code.toLowerCase().includes(lowerQuery);

      if (nameMatch || codeMatch) {
        filteredCountries.push(country);
      }
    }

    // Cache results with size limit (LRU-like: remove oldest when full)
    if (this.searchCache.size >= this.SEARCH_CACHE_MAX_SIZE) {
      // Remove oldest entry (first key in Map maintains insertion order)
      const oldestKey = this.searchCache.keys().next().value;

      this.searchCache.delete(oldestKey);
    }

    this.searchCache.set(cacheKey, filteredCountries);

    // Re-render filtered list
    this.renderFilteredCountries(filteredCountries, lowerQuery);
  }

  /**
   * Render filtered countries list
   */
  renderFilteredCountries(filteredCountries, query) {
    if (!this.countryList) return;

    if (!Array.isArray(filteredCountries) || filteredCountries.length === 0) {
      const noResultsText = this.getCountryNoResultsText();
      const safeNoResultsText = this.escapeHtml(noResultsText);

      this.countryList.innerHTML = `
        <li class="border-b border-gray-300 px-4 py-3 text-sm text-gray-500" role="status" aria-live="polite">
          ${safeNoResultsText}
        </li>
      `;

      return;
    }

    // Sort filtered countries with current country first, then alphabetically
    const sortedCountries = [...filteredCountries].sort((a, b) => {
      // Current country always first
      if (a.code === this.currentCountry) return -1;

      if (b.code === this.currentCountry) return 1;

      // Others alphabetically
      const nameA = typeof a.name === 'string' ? a.name : '';
      const nameB = typeof b.name === 'string' ? b.name : '';

      return nameA.localeCompare(nameB);
    });

    const html = sortedCountries.map((country) => this.renderCountryItem(country, query)).join('');

    this.countryList.innerHTML = html;

    // Bind click events
    this.bindCountryClicks();
  }

  /**
   * Show all countries
   */
  showAllCountries() {
    this.renderCountries();
  }

  /**
   * Escape HTML special characters to prevent XSS attacks
   * @param {string} str - String to escape
   * @returns {string} Escaped string safe for innerHTML
   */
  escapeHtml(str) {
    if (typeof str !== 'string') return '';

    const escapeMap = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#x27;',
    };

    return str.replace(/[&<>"']/g, (char) => escapeMap[char]);
  }

  /**
   * Get localized "no results" text for country search.
   * Uses window.theme.country_locale_switcher.country_no_results (injected by Liquid).
   * @returns {string}
   */
  getCountryNoResultsText() {
    const value = globalThis?.theme?.country_locale_switcher?.country_no_results;

    return typeof value === 'string' && value.trim().length > 0 ? value : 'No results found';
  }

  /**
   * Highlight matched text in country name
   * Security: escapes HTML to prevent XSS attacks
   * @param {string} text - Text to highlight
   * @param {string} query - Search query to highlight
   * @returns {string} HTML string with highlighted match
   */
  highlightText(text, query) {
    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const index = lowerText.indexOf(lowerQuery);

    // Escape the entire text if no match found
    if (index === -1) return this.escapeHtml(text);

    // Escape each part separately to prevent XSS
    const before = this.escapeHtml(text.slice(0, index));
    const match = this.escapeHtml(text.slice(index, index + query.length));
    const after = this.escapeHtml(text.slice(index + query.length));

    return `${before}<mark>${match}</mark>${after}`;
  }

  /**
   * Build redirect URL with test mode support and cross-domain parameters
   * Security: validates all inputs and restricts to allowed domains
   * @param {Object} storeConfig - Store configuration object
   * @param {string} locale - Target locale code
   * @returns {string} Redirect URL
   */
  buildRedirectUrl(storeConfig, locale) {
    if (!storeConfig) return '/';

    // Check if target store supports the requested locale
    // If not, fallback to target store's default locale
    let targetLocale = locale;

    if (storeConfig.availableLocales && !storeConfig.availableLocales.includes(locale)) {
      targetLocale = storeConfig.defaultLocale;
    }

    // Validate locale before using
    if (!this.validateLocale(targetLocale)) {
      console.error('[CountryLocaleSwitcher] Invalid locale for redirect:', targetLocale);

      return '/';
    }

    // Base URL: use previewUrl in test mode, regular url in production
    const baseUrl = this.testMode && storeConfig.previewUrl ? storeConfig.previewUrl : storeConfig.url;

    // Security: validate redirect URL is on allowed domain
    if (!this.validateRedirectDomain(baseUrl)) {
      console.error('[CountryLocaleSwitcher] Redirect blocked - invalid domain:', baseUrl);

      return '/';
    }

    // Check if locale is default for target store
    const isDefaultLocale = storeConfig.defaultLocale === targetLocale;
    const forceLocaleInPath = Boolean(storeConfig.forceLocaleInPath);

    try {
      const url = new URL(baseUrl);

      // Clean existing locale from pathname (important for test mode previewUrl)
      // previewUrl might already have locale like "/en" or "/de"
      const cleanPath = this.removeLocaleFromPath(url.pathname);

      // Add locale path if not default
      // Some stores might want locale always present in path, even for default locale,
      // to avoid Shopify SSR default language taking over at the root URL.
      if (forceLocaleInPath || !isDefaultLocale) {
        url.pathname = `/${targetLocale}${cleanPath}`;
      } else {
        url.pathname = cleanPath;
      }

      // Add cross-domain transfer parameters (validated before use)
      if (this.currentCountry && this.validateCountryCode(this.currentCountry)) {
        url.searchParams.set('selected_country', this.currentCountry);
      }

      // Use targetLocale (which may have been adjusted for target store compatibility)
      if (targetLocale && this.validateLocale(targetLocale)) {
        url.searchParams.set('selected_locale', targetLocale);
      }

      return url.toString();
    } catch (error) {
      console.error('[CountryLocaleSwitcher] Failed to build redirect URL:', error);

      return '/';
    }
  }

  /**
   * Validate redirect domain
   * Security: prevents open redirect attacks with strict domain matching
   * @param {string} url - URL to validate
   * @returns {boolean} True if domain is allowed
   */
  validateRedirectDomain(url) {
    const allowedDomains = ['inkposter.com', 'inkposter.eu', 'inkposter.co.uk', 'global.inkposter.com'];

    try {
      const parsedUrl = new URL(url);
      const { hostname } = parsedUrl;

      // Security: strict domain matching to prevent bypass via evil-inkposter.com
      const isAllowed = allowedDomains.some((domain) => {
        // Exact match
        if (hostname === domain) return true;

        // Subdomain match: hostname must end with ".domain" (with dot)
        // This prevents "evil-inkposter.com" from matching "inkposter.com"
        if (hostname.endsWith(`.${domain}`)) {
          // Ensure it's a proper subdomain (not part of another word)
          const prefix = hostname.slice(0, -(domain.length + 1));

          // Prefix should not be empty and should not contain suspicious chars
          return prefix.length > 0 && /^[a-z0-9-]+$/i.test(prefix);
        }

        return false;
      });

      if (!isAllowed) {
        console.warn('[CountryLocaleSwitcher] Blocked redirect to unauthorized domain:', hostname);
      }

      return isAllowed;
    } catch (error) {
      console.error('[CountryLocaleSwitcher] Invalid URL for validation:', url);

      return false;
    }
  }

  /**
   * Handle country selection
   * @param {string} countryCode - ISO country code
   */
  async selectCountry(countryCode) {
    try {
      // Check if countries data is loaded
      if (!this.countries || this.countries.length === 0) {
        console.error('[CountryLocaleSwitcher] Countries data not loaded');
        this.showError('Country data not available. Please refresh the page.');

        return;
      }

      const country = this.countries.find((c) => c.code === countryCode);

      if (!country) {
        console.error('[CountryLocaleSwitcher] Country not found:', countryCode);

        return;
      }

      // Save preference
      this.savePreference('country', countryCode);

      // Immediately update UI so the selected country's flag is visible
      // before we navigate away. This gives instant feedback to the user.
      this.currentCountry = countryCode;
      this.updateTriggerUI();

      // Get target store config
      const storeConfig = await this.getStoreConfig(country.store);

      // Select appropriate locale for the target country and store
      // For store-eu: DE/AT/CH → de, IT → it, ES → es, others → en
      const targetLocale = this.selectLocaleForCountry(countryCode, storeConfig);

      this.log('🎯 Manual country selection redirect', {
        country: countryCode,
        targetStore: storeConfig?.id,
        selectedLocale: targetLocale,
      });

      // Build redirect URL (automatically handles test mode)
      const redirectUrl = this.buildRedirectUrl(storeConfig, targetLocale);

      // Redirect
      window.location.href = redirectUrl;
    } catch (error) {
      console.error('[CountryLocaleSwitcher] Country selection failed:', error);
      this.showError('Failed to switch country. Please try again.');
    }
  }

  /**
   * Handle locale selection
   * Note: locale switching within same store doesn't need cross-domain params
   */
  selectLocale(locale) {
    try {
      // Validate locale before using
      if (!this.validateLocale(locale)) {
        console.error('[CountryLocaleSwitcher] Invalid locale selected:', locale);

        return;
      }

      // Save preference
      this.savePreference('locale', locale);

      // Check if locale is default for current store
      const isDefaultLocale = this.currentStoreConfig?.defaultLocale === locale;
      const forceLocaleInPath = Boolean(this.currentStoreConfig?.forceLocaleInPath);

      // In test mode, need to rebuild full URL with preview params
      if (this.testMode && this.currentStoreConfig?.previewUrl) {
        const url = new URL(this.currentStoreConfig.previewUrl);

        // Preserve current pathname (without locale prefix)
        const currentPath = this.removeLocaleFromPath(window.location.pathname);

        // Update pathname with locale
        if (forceLocaleInPath || !isDefaultLocale) {
          url.pathname = `/${locale}${currentPath}`;
        } else {
          url.pathname = currentPath;
        }

        // Preserve query parameters and hash from current URL
        const currentSearch = window.location.search;
        const currentHash = window.location.hash;

        // Merge preview URL params with current search params
        const currentParams = new URLSearchParams(currentSearch);

        // Current params take precedence (except preview-specific params)
        for (const [key, value] of currentParams) {
          if (!['_ab', '_fd', '_sc', 'preview_theme_id', 'key'].includes(key)) {
            url.searchParams.set(key, value);
          }
        }

        // Add hash if present
        if (currentHash) {
          url.hash = currentHash;
        }

        window.location.href = url.toString();

        return;
      }

      // Production mode: preserve current path, search params, and hash
      const path = this.removeLocaleFromPath(window.location.pathname);
      const { search, hash } = window.location; // Preserve query parameters and hash

      // Build redirect URL - don't add locale prefix if it's the default
      // Some stores might want locale always present in path.
      const localizedPath = forceLocaleInPath || !isDefaultLocale ? `/${locale}${path}` : path;
      const redirectUrl = `${localizedPath}${search}${hash}`;

      window.location.href = redirectUrl;
    } catch (error) {
      console.error('[CountryLocaleSwitcher] Locale selection failed:', error);
      this.showError('Failed to switch language. Please try again.');
    }
  }

  /**
   * Remove locale prefix from path
   */
  /**
   * Extract locale from URL path
   * @param {string} [path] - Path to extract locale from (defaults to current pathname)
   * @returns {string|null} Locale code or null if no locale in path
   */
  getLocaleFromPath(path = window.location.pathname) {
    if (typeof path !== 'string' || path.length === 0) {
      return null;
    }

    // Use dynamically loaded locales, fallback to hardcoded if not loaded
    const configLocales = this.availableLocales.length > 0 ? this.availableLocales : ['en', 'de', 'it', 'es', 'fr'];

    const domLocales = Array.from(this.querySelectorAll('[data-locale-code]'))
      .map((el) => el?.dataset?.localeCode)
      .filter((value) => typeof value === 'string' && value.trim().length > 0);

    const locales = Array.from(new Set([...configLocales, ...domLocales]));

    const lowerPath = path.toLowerCase();

    // Check if path starts with any known locale prefix
    for (const locale of locales) {
      const localePrefix = `/${String(locale).toLowerCase()}`;

      if (lowerPath === localePrefix || lowerPath.startsWith(`${localePrefix}/`)) {
        return locale;
      }
    }

    // No locale found in path
    return null;
  }

  removeLocaleFromPath(path) {
    if (typeof path !== 'string' || path.length === 0) {
      return '/';
    }

    // Use dynamically loaded locales, fallback to hardcoded if not loaded.
    // Also include locales rendered in the switcher UI to avoid mismatches
    // when store-config.json is incomplete.
    const configLocales = this.availableLocales.length > 0 ? this.availableLocales : ['en', 'de', 'it', 'es', 'fr'];

    const domLocales = Array.from(this.querySelectorAll('[data-locale-code]'))
      .map((el) => el?.dataset?.localeCode)
      .filter((value) => typeof value === 'string' && value.trim().length > 0);

    const locales = Array.from(new Set([...configLocales, ...domLocales]));

    const lowerPath = path.toLowerCase();

    for (const locale of locales) {
      const localePrefix = `/${String(locale).toLowerCase()}`;

      if (lowerPath.startsWith(localePrefix)) {
        // Remove locale prefix, but keep the rest of the path
        return path.slice(localePrefix.length) || '/';
      }
    }

    // Last-resort fallback: if the first URL segment looks like a locale (e.g., /pt-BR/...), strip it.
    // This prevents double-prefix issues when building localized URLs.
    const match = path.match(/^\/([^/]+)(\/|$)/);
    const firstSegment = match?.[1] || '';
    const looksLikeLocale = /^[a-z]{2}(?:-[a-z]{2})?$/i.test(firstSegment);

    if (looksLikeLocale) {
      const prefixLength = 1 + firstSegment.length;

      return path.slice(prefixLength) || '/';
    }

    // No locale found, return original path
    return path;
  }

  /**
   * Save preference to localStorage
   * @param {string} key - Preference key
   * @param {string} value - Preference value
   */
  savePreference(key, value) {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEY);
      let prefs = {};

      if (saved) {
        try {
          prefs = JSON.parse(saved);

          if (!prefs || typeof prefs !== 'object') {
            prefs = {};
          }
        } catch (parseError) {
          console.warn('[CountryLocaleSwitcher] Corrupted preferences, resetting:', parseError);
          prefs = {};
        }
      }

      prefs[key] = value;
      prefs.timestamp = Date.now();

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(prefs));
    } catch (error) {
      console.error('[CountryLocaleSwitcher] Failed to save preference:', error);
    }
  }

  /**
   * Get store configuration (uses cache to avoid repeated fetches)
   * @param {string} storeId - Store ID
   * @returns {Promise<Object>} Store configuration
   */
  async getStoreConfig(storeId) {
    try {
      // Use cached config if available
      if (this._storeConfigCache) {
        const cachedStore = this._storeConfigCache?.stores?.[storeId];

        return cachedStore ? { ...cachedStore, id: storeId } : undefined;
      }

      // Fallback: fetch config if cache is empty
      const url = `${this.assetBaseUrl}/store-config.json`;
      const config = await this.safeFetch(url);

      this._storeConfigCache = config;

      const store = config?.stores?.[storeId];

      return store ? { ...store, id: storeId } : undefined;
    } catch (error) {
      console.error('[CountryLocaleSwitcher] Failed to load store config:', error);
      throw error;
    }
  }

  /**
   * Toggle test mode (for DevTools console)
   * @returns {boolean} Current test mode state
   */
  toggleTestMode() {
    this.testMode = !this.testMode;

    if (this.testMode) {
      this.dataset.testMode = 'true';
    } else {
      delete this.dataset.testMode;
    }

    return this.testMode;
  }

  /**
   * Show error message
   */
  /**
   * Handle browser back/forward navigation
   * Syncs localStorage with current store to prevent stale data
   */
  handleBrowserNavigation() {
    try {
      // Get current hostname and pathname
      const { hostname, pathname } = window.location;

      // Reset to current page's actual country/locale from SSR data
      // This ensures localStorage doesn't override the actual page state
      const pageCountry = this.dataset.currentCountry;
      const pageLocale = this.dataset.currentLocale;

      // Extract locale from pathname (in case it changed)
      const pathParts = pathname.split('/').filter(Boolean);
      const potentialLocale = pathParts[0];

      // Update locale: use path locale if valid, otherwise use page default
      if (this.availableLocales.includes(potentialLocale)) {
        this.currentLocale = potentialLocale;
      } else {
        // No locale in path = default locale
        this.currentLocale = pageLocale || this.currentStoreConfig?.defaultLocale || 'en';
      }

      // Update country: always use the page's country (from Shopify localization)
      this.currentCountry = pageCountry;

      // Sync with localStorage to keep it updated
      this.savePreference('country', this.currentCountry);
      this.savePreference('locale', this.currentLocale);

      // Update UI to reflect current state
      this.updateTriggerUI();
    } catch (error) {
      console.error('[CountryLocaleSwitcher] Failed to handle browser navigation:', error);
    }
  }

  /**
   * Show error message to user
   */
  showError(message) {
    // Simple error display - can be enhanced
    alert(message);
  }
}

// Static flag for URL cleanup coordination across multiple instances
CountryLocaleSwitcher._urlCleanupScheduled = false;

// Shared fetch cache across all instances on the page
// Caches Promises to dedupe concurrent and repeated JSON requests.
CountryLocaleSwitcher._jsonFetchCache = new Map();

// Register custom element
customElements.define('country-locale-switcher', CountryLocaleSwitcher);

// Global helpers for DevTools console
if (typeof window !== 'undefined') {
  /**
   * Toggle test mode (preview URLs)
   * Usage: toggleCountryLocaleSwitcherTestMode()
   */
  window.toggleCountryLocaleSwitcherTestMode = () => {
    const switcher = document.querySelector('country-locale-switcher');

    if (!switcher) {
      console.error('❌ CountryLocaleSwitcher not found on page');

      return false;
    }

    return switcher.toggleTestMode();
  };

  /**
   * Toggle debug mode
   * Usage: toggleCountryLocaleSwitcherDebug() or toggleCountryLocaleSwitcherDebug(true/false)
   * @param {boolean} [enabled] - Optional: explicitly enable/disable
   */
  window.toggleCountryLocaleSwitcherDebug = (enabled) => {
    const switchers = document.querySelectorAll('country-locale-switcher');

    if (switchers.length === 0) {
      console.error('❌ CountryLocaleSwitcher not found on page');

      return false;
    }

    // If no argument, toggle current state
    const newState = enabled !== undefined ? enabled : !switchers[0].DEBUG;

    switchers.forEach((switcher) => {
      switcher.setDebugMode(newState);
    });

    return newState;
  };

  /**
   * Get current switcher state
   * Usage: getCountryLocaleSwitcherState()
   */
  window.getCountryLocaleSwitcherState = () => {
    const switcher = document.querySelector('country-locale-switcher');

    if (!switcher) {
      console.error('❌ CountryLocaleSwitcher not found on page');

      return null;
    }

    return {
      currentCountry: switcher.currentCountry,
      currentLocale: switcher.currentLocale,
      storeId: switcher.currentStoreConfig?.id,
      testMode: switcher.testMode,
      debugMode: switcher.DEBUG,
      browserLanguages: navigator.languages,
      detectedBrowserLocale: switcher.detectBrowserLocale?.() || null,
      localStorage: (() => {
        try {
          return JSON.parse(localStorage.getItem(switcher.STORAGE_KEY));
        } catch {
          return null;
        }
      })(),
      sessionStorage: {
        ipCountry: sessionStorage.getItem('inkposter_ip_country'),
      },
    };
  };

  // Log available commands
  console.info(`
🌍 CountryLocaleSwitcher Console Commands:
  - toggleCountryLocaleSwitcherDebug()     → Toggle debug logging
  - toggleCountryLocaleSwitcherDebug(true) → Enable debug logging
  - toggleCountryLocaleSwitcherTestMode()  → Toggle test mode (preview URLs)
  - getCountryLocaleSwitcherState()        → Get current state
  `);
}

export default CountryLocaleSwitcher;
